<template>
  <div class="app-container">
    <v-app class="modern-app">
      <v-app-bar app flat class="app-bar">
        <router-link to="/" class="brand-wrap" aria-label="OnlyGrades">
          <span class="brand-dot" aria-hidden="true"></span>
          <v-toolbar-title class="title">{{ t("title") }}</v-toolbar-title>
        </router-link>
        <v-spacer></v-spacer>
        <template v-if="!mdAndDown">
          <v-btn class="nav-btn" text :to="contextRoute('/')">{{
            state.locale === "no" ? "Sammenlign" : "Compare"
          }}</v-btn>
          <v-btn class="nav-btn" text :to="contextRoute('/explore')">{{
            state.locale === "no" ? "Finn emner" : "Find courses"
          }}</v-btn>
          <v-btn
            class="nav-btn"
            text
            :to="contextRoute('/easiest-hardest-courses')"
            >{{ state.locale === "no" ? "Rangeringer" : "Rankings" }}</v-btn
          >
          <v-btn
            v-if="state.features.advancedEnabled"
            class="nav-btn"
            text
            to="/advanced"
            >{{ t("message.advancedNav") }}</v-btn
          >
          <v-btn class="nav-btn nav-btn-primary" text to="/premium">{{
            t("message.premiumNav")
          }}</v-btn>
          <v-btn
            v-if="state.features.premiumEnabled"
            class="nav-btn"
            text
            to="/account"
          >
            {{ t("message.accountMenu") }}
          </v-btn>
          <v-btn class="nav-btn" text to="/about">{{
            t("message.about")
          }}</v-btn>
          <v-menu location="bottom end" offset="8">
            <template #activator="{ props }">
              <v-btn
                class="language-icon-btn"
                icon
                variant="text"
                :aria-label="
                  state.locale === 'no' ? 'Bytt språk' : 'Change language'
                "
                v-bind="props"
              >
                <v-icon size="20">mdi-translate</v-icon>
              </v-btn>
            </template>
            <v-list class="language-menu" density="compact">
              <v-list-item
                :active="state.locale === 'en'"
                @click="setLanguage('en')"
              >
                <v-list-item-title>English</v-list-item-title>
              </v-list-item>
              <v-list-item
                :active="state.locale === 'no'"
                @click="setLanguage('no')"
              >
                <v-list-item-title>Norsk</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-menu v-if="state.user" location="bottom end" offset="8">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                class="user-pill"
                variant="text"
                size="small"
              >
                {{ state.user.email }}
              </v-btn>
            </template>
            <v-list class="account-menu" density="compact">
              <v-list-item
                v-if="state.features.premiumEnabled"
                :to="'/account'"
                :title="t('message.accountMenu')"
              ></v-list-item>
              <v-list-item
                @click="handleLogout"
                :title="t('message.logout')"
              ></v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            v-if="state.user"
            class="nav-btn nav-btn-logout"
            text
            @click="handleLogout"
          >
            {{ t("message.logout") }}
          </v-btn>
        </template>
        <template v-else>
          <v-menu location="bottom end" offset="8">
            <template #activator="{ props }">
              <v-btn
                class="language-icon-btn mobile-language-icon-btn"
                icon
                variant="text"
                :aria-label="
                  state.locale === 'no' ? 'Bytt språk' : 'Change language'
                "
                v-bind="props"
              >
                <v-icon size="19">mdi-translate</v-icon>
              </v-btn>
            </template>
            <v-list class="language-menu" density="compact">
              <v-list-item
                :active="state.locale === 'en'"
                @click="setLanguage('en')"
              >
                <v-list-item-title>English</v-list-item-title>
              </v-list-item>
              <v-list-item
                :active="state.locale === 'no'"
                @click="setLanguage('no')"
              >
                <v-list-item-title>Norsk</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            v-if="state.user"
            class="menu-btn mobile-logout-btn"
            icon
            variant="text"
            :aria-label="t('message.logout')"
            @click="handleLogout"
          >
            <v-icon>mdi-logout</v-icon>
          </v-btn>
          <v-btn
            class="menu-btn"
            icon
            variant="text"
            :aria-label="state.locale === 'no' ? 'Åpne meny' : 'Open menu'"
            @click="mobileMenuOpen = true"
          >
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </template>
      </v-app-bar>
      <v-navigation-drawer
        v-if="mdAndDown"
        v-model="mobileMenuOpen"
        :inert="!mobileMenuOpen || undefined"
        :aria-hidden="!mobileMenuOpen"
        location="right"
        temporary
        class="mobile-drawer"
      >
        <div class="drawer-header">
          <div class="drawer-brand">
            <span class="brand-dot" aria-hidden="true"></span>
            <span class="drawer-title">{{ t("title") }}</span>
          </div>
          <v-btn
            icon
            variant="text"
            :aria-label="state.locale === 'no' ? 'Lukk meny' : 'Close menu'"
            @click="mobileMenuOpen = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-list nav class="drawer-nav">
          <v-list-item
            rounded="lg"
            :to="contextRoute('/')"
            @click="mobileMenuOpen = false"
            :title="state.locale === 'no' ? 'Sammenlign' : 'Compare'"
            prepend-icon="mdi-home-outline"
          ></v-list-item>
          <v-list-item
            rounded="lg"
            :to="contextRoute('/explore')"
            @click="mobileMenuOpen = false"
            :title="state.locale === 'no' ? 'Finn emner' : 'Find courses'"
            prepend-icon="mdi-magnify"
          ></v-list-item>
          <v-list-item
            rounded="lg"
            :to="contextRoute('/easiest-hardest-courses')"
            @click="mobileMenuOpen = false"
            :title="state.locale === 'no' ? 'Rangeringer' : 'Rankings'"
            prepend-icon="mdi-chart-box-outline"
          ></v-list-item>
          <v-list-item
            v-if="state.features.advancedEnabled"
            rounded="lg"
            :to="'/advanced'"
            @click="mobileMenuOpen = false"
            :title="t('message.advancedNav')"
            prepend-icon="mdi-flask-outline"
          ></v-list-item>
          <v-list-item
            rounded="lg"
            :to="'/premium'"
            @click="mobileMenuOpen = false"
            :title="t('message.premiumNav')"
            prepend-icon="mdi-star-outline"
          ></v-list-item>
          <v-list-item
            v-if="state.features.premiumEnabled"
            rounded="lg"
            :to="'/account'"
            @click="mobileMenuOpen = false"
            :title="t('message.accountMenu')"
            prepend-icon="mdi-account-circle-outline"
          ></v-list-item>
          <v-list-item
            rounded="lg"
            :to="'/about'"
            @click="mobileMenuOpen = false"
            :title="t('message.about')"
            prepend-icon="mdi-information-outline"
          ></v-list-item>
        </v-list>
        <div class="drawer-footer">
          <span v-if="state.user" class="user-pill drawer-user">{{
            state.user.email
          }}</span>
          <v-btn
            v-if="state.user"
            class="toggle-button drawer-logout-btn"
            @click="handleLogout"
          >
            {{ t("message.logout") }}
          </v-btn>
        </div>
      </v-navigation-drawer>
      <v-main class="main-content">
        <nav
          v-if="mdAndDown"
          class="task-navigation"
          :aria-label="state.locale === 'no' ? 'Arbeidsområder' : 'Workspaces'"
        >
          <router-link :to="contextRoute('/explore')"
            ><v-icon size="17" aria-hidden="true">mdi-magnify</v-icon
            >{{
              state.locale === "no" ? "Finn emner" : "Find courses"
            }}</router-link
          >
          <router-link
            :to="contextRoute('/')"
            :class="{
              'router-link-exact-active': $route.name === 'homeWithCourse',
            }"
            ><v-icon size="17" aria-hidden="true">mdi-chart-bar</v-icon
            >{{ state.locale === "no" ? "Sammenlign" : "Compare" }}</router-link
          >
          <router-link :to="contextRoute('/easiest-hardest-courses')"
            ><v-icon size="17" aria-hidden="true">mdi-sort</v-icon
            >{{
              state.locale === "no" ? "Rangeringer" : "Rankings"
            }}</router-link
          >
        </nav>
        <v-container fluid>
          <router-view />
        </v-container>
      </v-main>
      <div
        v-if="showCookieBanner"
        class="cookie-banner"
        role="dialog"
        aria-live="polite"
      >
        <div class="cookie-banner-content">
          <p class="cookie-banner-text">
            {{ t("message.cookies.bannerText") }}
            <router-link class="legal-link" to="/privacy">
              {{ t("message.cookies.learnMore") }}
            </router-link>
          </p>
          <div class="cookie-banner-actions">
            <button
              class="cookie-btn cookie-btn-secondary"
              @click="
                setAnalyticsConsent(state.analyticsConsentConfig.rejected)
              "
            >
              {{ t("message.cookies.reject") }}
            </button>
            <button
              class="cookie-btn cookie-btn-primary"
              @click="
                setAnalyticsConsent(state.analyticsConsentConfig.accepted)
              "
            >
              {{ t("message.cookies.accept") }}
            </button>
          </div>
        </div>
      </div>
      <footer class="app-footer">
        <div class="footer-inner">
          <div>© 2026 OnlyGrades - {{ t("message.allRightsReserved") }}</div>
          <div class="legal-links">
            <router-link class="legal-link" to="/privacy">
              {{ t("message.privacyNav") }}
            </router-link>
            <router-link class="legal-link" to="/terms">
              {{ t("message.termsNav") }}
            </router-link>
            <button
              v-if="state.features.analyticsEnabled"
              class="cookie-settings-btn"
              @click="resetAnalyticsConsent"
            >
              {{ t("message.cookies.settings") }}
            </button>
          </div>
          <div class="attribution-text">
            {{ t("message.dataAttribution") }}
          </div>
        </div>
      </footer>
    </v-app>
  </div>
</template>

<script>
import { computed, inject, ref } from "vue";
import { courseSelection } from "@/courseSelection";
import { useDisplay } from "vuetify";
import ApiService from "@/ApiService";

const DEFAULT_SITE_URL = "https://www.onlygrades.no";

export default {
  name: "App",
  setup() {
    const state = inject("state");
    const mobileMenuOpen = ref(false);
    const { mdAndDown } = useDisplay();
    const rawSiteUrl = (
      process.env.VUE_APP_SITE_URL || DEFAULT_SITE_URL
    ).trim();
    const siteUrl = (rawSiteUrl || DEFAULT_SITE_URL).replace(/\/+$/, "");

    const t = (key) => {
      const keys = key.split(".");
      let value = state.translations[state.locale];
      keys.forEach((k) => {
        value = value[k];
      });
      return value;
    };

    const setLanguage = (lang) => {
      state.locale = lang;
    };

    const ensureLeadingSlash = (value) => {
      if (!value) return "/";
      return String(value).startsWith("/") ? String(value) : `/${value}`;
    };

    const resolveSeoValue = (value, route, fallback = "") => {
      try {
        if (typeof value === "function") {
          const resolved = value(route, t);
          return resolved == null ? fallback : String(resolved);
        }
        if (value == null || value === "") {
          return fallback;
        }
        return String(value);
      } catch {
        return fallback;
      }
    };

    const upsertMetaTag = ({ name, property }, content) => {
      const attributeName = name ? "name" : "property";
      const attributeValue = name || property;
      if (!attributeValue) return;
      let tag = document.head.querySelector(
        `meta[${attributeName}="${attributeValue}"]`
      );
      if (!content) {
        if (tag) tag.remove();
        return;
      }
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attributeName, attributeValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", String(content));
    };

    const upsertLinkTag = ({ rel }, href) => {
      if (!rel) return;
      let tag = document.head.querySelector(`link[rel="${rel}"]`);
      if (!href) {
        if (tag) tag.remove();
        return;
      }
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute("href", href);
    };

    const upsertStructuredData = (id, payload) => {
      let tag = document.head.querySelector(`script#${id}`);
      if (!payload) {
        if (tag) tag.remove();
        return;
      }
      if (!tag) {
        tag = document.createElement("script");
        tag.id = id;
        tag.type = "application/ld+json";
        document.head.appendChild(tag);
      }
      tag.textContent = JSON.stringify(payload);
    };

    const applySeoForRoute = (route) => {
      const matchedRecords = Array.isArray(route?.matched) ? route.matched : [];
      const activeRecord =
        [...matchedRecords].reverse().find((record) => record?.meta?.seo) ||
        null;
      const seo = activeRecord?.meta?.seo || {};

      const resolvedTitle = resolveSeoValue(
        seo.title,
        route,
        seo.titleKey ? t(seo.titleKey) : t("title")
      );
      const resolvedDescription = resolveSeoValue(
        seo.description,
        route,
        seo.descriptionKey ? t(seo.descriptionKey) : t("seo.defaultDescription")
      );
      const resolvedRobots = resolveSeoValue(seo.robots, route, "index,follow");
      const resolvedCanonicalPath = resolveSeoValue(
        seo.canonicalPath,
        route,
        route?.path || "/"
      );
      const canonicalUrl = /^https?:\/\//i.test(resolvedCanonicalPath)
        ? resolvedCanonicalPath
        : `${siteUrl}${ensureLeadingSlash(resolvedCanonicalPath)}`;
      const localeCode = state.locale === "no" ? "nb" : "en";
      const ogLocale = state.locale === "no" ? "nb_NO" : "en_US";
      const schemaType = resolveSeoValue(seo.schemaType, route, "WebPage");

      const structuredData =
        schemaType === "WebSite"
          ? {
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "OnlyGrades",
              url: siteUrl,
              inLanguage: localeCode,
            }
          : schemaType === "Course"
          ? {
              "@context": "https://schema.org",
              "@type": "Course",
              name: resolveSeoValue(
                () =>
                  String(
                    route?.params?.courseCode || t("seo.course.unknownCode")
                  ),
                route,
                t("seo.course.unknownCode")
              ),
              description: resolvedDescription,
              url: canonicalUrl,
              provider: {
                "@type": "Organization",
                name: "OnlyGrades",
                url: siteUrl,
              },
            }
          : {
              "@context": "https://schema.org",
              "@type": schemaType,
              name: resolvedTitle,
              description: resolvedDescription,
              url: canonicalUrl,
              isPartOf: {
                "@type": "WebSite",
                name: "OnlyGrades",
                url: siteUrl,
              },
            };

      document.documentElement.setAttribute("lang", localeCode);
      document.title = resolvedTitle;
      upsertMetaTag({ name: "description" }, resolvedDescription);
      upsertMetaTag({ name: "robots" }, resolvedRobots);
      upsertMetaTag({ property: "og:type" }, "website");
      upsertMetaTag({ property: "og:site_name" }, "OnlyGrades");
      upsertMetaTag({ property: "og:locale" }, ogLocale);
      upsertMetaTag({ property: "og:title" }, resolvedTitle);
      upsertMetaTag({ property: "og:description" }, resolvedDescription);
      upsertMetaTag({ property: "og:url" }, canonicalUrl);
      upsertMetaTag({ name: "twitter:card" }, "summary");
      upsertMetaTag({ name: "twitter:title" }, resolvedTitle);
      upsertMetaTag({ name: "twitter:description" }, resolvedDescription);
      upsertLinkTag({ rel: "canonical" }, canonicalUrl);
      upsertStructuredData("onlygrades-structured-data", structuredData);
    };

    const showCookieBanner = computed(
      () => state.features.analyticsEnabled && !state.analyticsConsent
    );

    const setAnalyticsConsent = (value) => {
      const config = state.analyticsConsentConfig || {};
      const { key, accepted, rejected, eventName } = config;
      if (value !== accepted && value !== rejected) return;
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.warn("Failed saving analytics consent", error);
      }
      state.analyticsConsent = value;
      if (eventName) {
        window.dispatchEvent(new Event(eventName));
      }
    };

    const resetAnalyticsConsent = () => {
      const config = state.analyticsConsentConfig || {};
      const { key, eventName } = config;
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn("Failed clearing analytics consent", error);
      }
      state.analyticsConsent = null;
      if (eventName) {
        window.dispatchEvent(new Event(eventName));
      }
    };

    const clearAuthState = () => {
      state.authToken = "";
      state.user = null;
      state.entitlement = { plan: "free", status: "free", is_paid: false };
      try {
        localStorage.removeItem(state.authStorageKeys.token);
        localStorage.removeItem(state.authStorageKeys.user);
        localStorage.removeItem(state.authStorageKeys.entitlement);
      } catch (error) {
        void error;
      }
    };

    const handleLogout = async () => {
      try {
        if (state.authToken) {
          await ApiService.logout();
        }
      } catch (error) {
        void error;
      } finally {
        clearAuthState();
      }
    };

    return {
      t,
      state,
      setLanguage,
      mobileMenuOpen,
      mdAndDown,
      showCookieBanner,
      setAnalyticsConsent,
      resetAnalyticsConsent,
      handleLogout,
      applySeoForRoute,
    };
  },
  watch: {
    $route(to) {
      this.applySeoForRoute(to);
      this.mobileMenuOpen = false;
    },
    "state.locale"() {
      this.applySeoForRoute(this.$route);
    },
  },
  methods: {
    contextRoute(path) {
      const query = {};
      const institution = courseSelection.state.initialized
        ? courseSelection.state.institution
        : this.$route.query.institution;
      const program = courseSelection.state.initialized
        ? courseSelection.state.program
        : this.$route.query.program;
      if (institution) query.institution = institution;
      if (institution && program) query.program = program;
      return { path, query };
    },
  },
  mounted() {
    this.applySeoForRoute(this.$route);
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&family=Newsreader:wght@400;500;600;700&display=swap");

:root {
  --og-bg: #f7f8f2;
  --og-surface: #ffffff;
  --og-surface-soft: #eff3e9;
  --og-text: #20362a;
  --og-muted: #657164;
  --og-border: #dce3d6;
  --og-primary: #326348;
  --og-primary-strong: #214c35;
  --og-primary-soft: #e8efde;
  --og-primary-soft-hover: #d9e6cb;
  --og-secondary: #213f30;
  --og-secondary-soft: #edf2e6;
  --og-accent: #d4ed87;
  --og-accent-soft: #f0f6de;
  --og-link: #326348;
  --og-shadow: 0 10px 26px rgba(17, 24, 39, 0.08);
  --og-app-bg-image: url("~@/assets/generated/matte-app-bg.svg");
  --og-surface-noise-image: url("~@/assets/generated/matte-surface-noise.svg");
}

html,
body {
  background: var(--og-bg);
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.modern-app {
  background: transparent !important;
  min-height: 100vh !important;
  display: flex;
  flex-direction: column;
}
.task-navigation {
  display: flex;
  justify-content: center;
  gap: 7px;
  background: #f7f8f2;
  border-bottom: 1px solid #dce3d6;
  padding: 9px 12px;
}
.task-navigation a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 40px;
  padding: 8px 15px;
  border-radius: 7px;
  color: #60705a;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
}
.task-navigation a.router-link-exact-active {
  background: #e4ecd9;
  color: #244d33;
}
.task-navigation a:focus-visible {
  outline: 3px solid #326348;
  outline-offset: 2px;
}
@media (max-width: 420px) {
  .task-navigation {
    gap: 3px;
    padding: 6px 8px;
  }
  .task-navigation a {
    flex: 1;
    gap: 5px;
    font-size: 11px;
    padding-inline: 5px;
  }
}

#app {
  font-family: "Work Sans", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--og-text);
  display: flex;
  flex-direction: column;
  flex: 1;
}

.title {
  font-size: 1.35rem;
  color: var(--og-text);
  font-family: "Work Sans", sans-serif;
  font-weight: 600;
}

.brand-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--og-text);
}

.brand-dot {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: 31px;
  height: 31px;
  border-radius: 8px;
  background: #d4ed87;
}
.brand-dot::before {
  content: "";
  position: absolute;
  left: 8px;
  bottom: 8px;
  width: 4px;
  height: 8px;
  border-radius: 1px;
  background: #274c35;
  box-shadow: 6px -4px 0 #274c35, 12px -9px 0 #274c35;
}

.app-bar {
  background: #f7f8f2f5 !important;
  border-bottom: 1px solid var(--og-border);
  padding-inline: 24px;
  backdrop-filter: blur(14px);
}

.app-bar .v-toolbar__content {
  max-width: 1140px;
  margin: 0 auto;
  width: 100%;
  gap: 6px;
  min-height: 66px;
}

.app-footer {
  padding: 26px 24px;
  background: #edf1e7 !important;
  border-top: 1px solid var(--og-border);
  color: var(--og-muted);
  margin-top: auto;
  width: 100%;
}

.footer-inner {
  max-width: 1140px;
  margin: 0 auto;
  text-align: left;
}

.nav-btn {
  margin: 0 4px;
  border-radius: 999px;
  color: #243240 !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  min-width: auto;
  height: 36px;
  padding-inline: 12px;
}

.nav-btn:hover {
  background: var(--og-primary-soft);
}

.nav-btn-primary {
  background: var(--og-primary);
  color: #ffffff !important;
  border: 1px solid var(--og-primary);
}

.nav-btn-primary:hover {
  background: var(--og-primary-strong);
  border-color: var(--og-primary-strong);
}

.nav-btn-logout {
  border: 1px solid var(--og-border);
  background: var(--og-surface-soft);
}

.nav-btn-logout:hover {
  border-color: var(--og-primary);
}

.language-icon-btn {
  margin-left: 4px;
  color: var(--og-muted);
}

.language-icon-btn:hover {
  color: var(--og-text);
  background: var(--og-primary-soft);
}

.mobile-language-icon-btn {
  margin-right: 2px;
}

.language-menu {
  min-width: 136px;
  border: 1px solid var(--og-border);
  border-radius: 10px;
  background: var(--og-surface);
}

.v-toolbar-title {
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.8px;
}

.user-pill {
  margin-left: 10px;
  font-size: 0.75rem;
  color: var(--og-text);
  background: var(--og-secondary-soft);
  border: 1px solid var(--og-border);
  border-radius: 999px;
  padding: 6px 10px;
}

.legal-links {
  margin-top: 6px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.legal-link {
  color: var(--og-link);
  text-decoration: none;
  font-size: 0.85rem;
}

.legal-link:hover {
  text-decoration: underline;
}

.cookie-settings-btn {
  border: 0;
  background: transparent;
  color: var(--og-link);
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
}

.cookie-settings-btn:hover {
  text-decoration: underline;
}

.attribution-text {
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--og-muted);
}

.cookie-banner {
  position: fixed;
  left: 14px;
  right: 14px;
  bottom: 14px;
  z-index: 2100;
  display: flex;
  justify-content: center;
}

.cookie-banner-content {
  width: min(980px, 100%);
  background: var(--og-surface);
  border: 1px solid var(--og-border);
  border-radius: 12px;
  box-shadow: var(--og-shadow);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cookie-banner-text {
  margin: 0;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.85rem;
}

.cookie-banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.cookie-btn {
  min-height: 34px;
  border-radius: 999px;
  border: 1px solid var(--og-border);
  padding: 0 12px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.cookie-btn-secondary {
  background: var(--og-surface-soft);
  color: var(--og-text);
}

.cookie-btn-primary {
  background: var(--og-primary);
  border-color: var(--og-primary);
  color: #ffffff;
}

.cookie-btn-primary:hover {
  background: var(--og-primary-strong);
  border-color: var(--og-primary-strong);
}

.main-content {
  flex: 1 0 auto;
  padding-top: var(--v-layout-top, 0px);
}

.menu-btn {
  color: var(--og-text);
}

.mobile-logout-btn {
  margin-right: 2px;
}

.mobile-drawer {
  border-left: 1px solid var(--og-border);
}

.mobile-drawer :deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  padding: 10px 10px calc(env(safe-area-inset-bottom, 0px) + 10px);
  gap: 8px;
  background: var(--og-surface);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 2px;
}

.drawer-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.drawer-title {
  font-family: "Work Sans", sans-serif;
  font-size: 1rem;
  color: var(--og-text);
}

.drawer-nav {
  padding-top: 8px;
}

.drawer-nav :deep(.v-list-item) {
  min-height: 46px;
}

.drawer-footer {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 2px 2px;
}

.drawer-user {
  display: inline-flex;
  align-self: flex-start;
}

@media (max-width: 600px) {
  .app-bar .v-toolbar__content {
    gap: 2px;
    min-height: 60px;
  }
  .brand-dot {
    width: 31px;
    height: 31px;
  }
  .app-bar {
    padding-inline: 16px;
  }
  .title {
    font-size: 1rem;
  }
  .nav-btn {
    font-size: 0.8rem;
    margin: 2px;
    padding-inline: 8px;
    height: 34px;
  }
  .app-footer {
    padding: 10px 8px;
  }
  .cookie-banner {
    left: 8px;
    right: 8px;
    bottom: 8px;
  }
  .cookie-banner-content {
    padding: 10px;
    align-items: flex-start;
    flex-direction: column;
  }
  .cookie-banner-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .drawer-nav :deep(.v-list-item-title) {
    font-size: 0.94rem;
  }
  .user-pill {
    display: none;
  }
}
</style>
